import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import getBuInfo from '../../api/shop/buInfo';
import createConsignment from '../../api/cars/createConsignment';
import HookForm from '../../components/HookForm/HookForm';
import Button from '../../components/Button/Button';
import LoaderInline from '../../components/Loader/LoaderInline';
import formatNumber from '../../utils/formatNumber';
import extractErrorMessage from '../../utils/extractErrorMessage';
import alert from '../../helpers/alert';
import {
  REQUISITES_INPUTS,
  SELLER_REQUISITES_INPUTS,
  cleanBank,
  sellerFio,
  withDefaults,
} from './NewCar/Consignment/requisitesInputs';

const reqGrid = { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr 1fr' };

const Section = ({ title, children }) => (
  <div style={{ padding: '10px 20px', backgroundColor: 'white', marginTop: 20, border: '1px solid #E0E0E0' }}>
    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#8C6A3B' }}>{title}</div>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', padding: '6px 0', borderBottom: '1px solid #F1E9DC' }}>
    <div style={{ color: '#9C8A73' }}>{label}</div>
    <div style={{ color: '#333' }}>{value || '—'}</div>
  </div>
);

const CarBuInfo = () => {
  const { id } = useParams();
  const dcFormRef = useRef(null);
  const sellerFormRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getBuInfo(id)
      .then((res) => setData(res?.data || null))
      .catch((e) => setError(e?.message || 'Ошибка загрузки данных'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const consignment = data?.consignment;
  const owner = consignment?.seller || {};
  // Серый дилер продаёт сам: вместо клиента-доверителя — ДЦ (sellerDc), клиента нет.
  const sellerDc = consignment?.sellerDc || null;
  const dc = consignment?.requisites || {};
  const bank = consignment?.sellerRequisites || {};
  const copiedFromDc = Boolean(bank.iban && dc.iban && cleanBank(bank.iban) === cleanBank(dc.iban));

  // key и дефолты считаем из одних и тех же значений: иначе после сохранения
  // форма перемонтируется со старыми defaultValue, и менеджеру кажется, что
  // реквизиты не сохранились.
  const sellerDefaults = {
    recipient: bank.recipient || sellerFio(owner),
    iban: copiedFromDc ? '' : bank.iban,
    bankName: copiedFromDc ? '' : bank.bankName,
    bic: copiedFromDc ? '' : bank.bic,
  };
  const dcKey = [dc.dcName, dc.bin, dc.address, dc.iban, dc.bankName, dc.bic].join('|');
  const sellerKey = [sellerDefaults.recipient, sellerDefaults.iban, sellerDefaults.bankName, sellerDefaults.bic].join('|');
  const dcInputs = withDefaults(REQUISITES_INPUTS, dc);
  const sellerInputs = withDefaults(SELLER_REQUISITES_INPUTS, sellerDefaults);

  const saveConsignment = async () => {
    const requisites = await dcFormRef.current.submit();
    if (!requisites) return;
    const sellerRequisites = await sellerFormRef.current.submit();
    if (!sellerRequisites) return;
    if (!owner.id && !sellerDc) {
      alert.error('Не указан продавец');
      return;
    }

    setSaving(true);
    try {
      await createConsignment({
        carId: Number(id),
        ...(sellerDc ? { sellerDcId: sellerDc.id } : { sellerClientId: owner.id }),
        price: consignment.price,
        requisites,
        sellerRequisites: {
          recipient: sellerRequisites.recipient,
          iban: cleanBank(sellerRequisites.iban),
          bankName: sellerRequisites.bankName,
          bic: cleanBank(sellerRequisites.bic),
        },
      });
      alert.success('Реквизиты сохранены');
      load();
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '10px 20px', backgroundColor: 'white', marginTop: '20px', border: '1px solid #E0E0E0' }}>
        <LoaderInline />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '10px 20px', backgroundColor: 'white', marginTop: '20px', border: '1px solid #E0E0E0', color: '#D14343' }}>
        {error}
      </div>
    );
  }

  const seller = data?.seller || data?.Seller || {};
  const buyer = data?.buyer || data?.Buyer || {};

  if (consignment) {
    const fio = sellerFio(owner);

    return (
      <div style={{ padding: '0 20px 20px 20px' }}>
        <Section title={'Продавец по договору поручения'}>
          {sellerDc && <Row label={'Дилерский центр'} value={sellerDc.name} />}
          {sellerDc && <Row label={'БИН'} value={sellerDc.bin} />}
          {sellerDc && <Row label={'Директор'} value={sellerDc.director} />}
          {!sellerDc && (
            <Row
              label={'ФИО'}
              value={
                owner.id ? (
                  <a href={`/client/${owner.id}/main-data`} style={{ color: '#B45436' }}>
                    {fio || `Клиент #${owner.id}`}
                  </a>
                ) : (
                  fio
                )
              }
            />
          )}
          {!sellerDc && <Row label={'ИИН'} value={owner.iin} />}
          {!sellerDc && <Row label={'Номер телефона'} value={owner.phone} />}
          <Row label={'Цена по договору'} value={consignment.price ? `${formatNumber(consignment.price)} ₸` : ''} />
          <Row
            label={'Дата оформления'}
            value={consignment.createdAt ? dayjs(consignment.createdAt).format('DD.MM.YYYY') : ''}
          />
        </Section>

        <HookForm
          key={`seller-${sellerKey}`}
          ref={sellerFormRef}
          border
          title="Счёт продавца"
          inputs={sellerInputs}
          grid={reqGrid}
        />
        <HookForm
          key={`dc-${dcKey}`}
          ref={dcFormRef}
          border
          title="Реквизиты дилерского центра"
          inputs={dcInputs}
          grid={reqGrid}
        />
        <div style={{ marginTop: 16 }}>
          <Button text={saving ? 'Сохраняем…' : 'Сохранить'} fill onClick={saveConsignment} disabled={saving} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 20px 20px 20px' }}>
      <Section title={'Данные продавца'}>
        <Row label={'Фамилия'} value={seller.LastName || seller.lastName} />
        <Row label={'Имя'} value={seller.FirstName || seller.firstName} />
        <Row label={'Отчество'} value={seller.MiddleName || seller.middleName} />
        <Row label={'ИИН'} value={seller.IIN || seller.iin} />
        <Row label={'Номер удостоверения личности'} value={seller.DOCUMENT_NUMBER || seller.documenT_NUMBER} />
        <Row label={'Дата выдачи документа'} value={(seller.DOCUMENT_GIVED_DATE || seller.documenT_GIVED_DATE) ? dayjs(seller.DOCUMENT_GIVED_DATE || seller.documenT_GIVED_DATE).format('DD.MM.YYYY') : ''} />
        <Row label={'Кем выдан'} value={seller.DOCUMENT_ISSUED_BY || seller.documenT_ISSUED_BY} />
        <Row label={'Адрес'} value={seller.ADDRESS || seller.address} />
        <Row label={'Номер телефона'} value={seller.CONTACT_PHONE || seller.contacT_PHONE} />
      </Section>

      <Section title={'Данные покупателя (юр. лицо)'}>
        <Row label={'Наименование юр. лица'} value={buyer.JuridicalName || buyer.juridicalName} />
        <Row label={'ФИО директора'} value={buyer.JuridicalDirectorName || buyer.juridicalDirectorName} />
        <Row label={'Реквизиты компании'} value={buyer.CompanyDetails || buyer.companyDetails} />
        <Row label={'Номер договора'} value={buyer.ContractNumber || buyer.contractNumber} />
        <Row label={'Список работ'} value={buyer.RepairWorkList || buyer.repairWorkList} />
      </Section>

      <Section title={'Банковские реквизиты'}>
        <Row label={'Номер банковского счёта'} value={seller.bankAccount} />
        <Row label={'Наименование банка'} value={seller.bankName} />
        <Row label={'БИК'} value={seller.bankBic} />
      </Section>
    </div>
  );
};

export default CarBuInfo;
